import React, { useState } from 'react';
import { useRecoilState } from 'recoil';
import { trackEvent as segmentTrackEvent, EVENTS, SCREENS } from '@app/segment';
import {
  Modal,
  ModalContent,
  ModalOverlay,
  useMultiStyleConfig,
  ModalCloseButton,
  Desktop,
  MobileAndTablet,
  useBreakpointValue,
  Box,
} from '@stockx/ui';
import { ChevronLeftIcon, ChevronRightIcon } from '@stockx/icons';
import { useGATrackEvent, Events, Categories } from '@app/analytics';
import { Global, css } from '@emotion/react';
import UserGeneratedContentModalMobile from './Mobile/ModalMobile';
import UserGeneratedContentModalDesktop from './Desktop/ModalDesktop';
import { StyledModalArrowStyles } from './UserGeneratedContentModal.styles';
import UserGeneratedContentSocialLinks from './UserGeneratedContentSocialLinks';
import currentIndexState from '../../states/currentIndexState';
import entrieState from '../../states/entrieState';

const MobileModalHeightStyles = css`
  .chakra-modal__content-container {
    height: 100% !important;
  }
`;

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

const UserGeneratedContentModal: React.FC<Props> = ({ isOpen, onClose }) => {
  const { isMobile, isTablet } = useBreakpointValue();
  const size = !isMobile && !isTablet ? 'lg' : 'full';
  const styles = useMultiStyleConfig('Modal', { size });
  const [currentIndex, setCurrentIndex] = useRecoilState(currentIndexState);
  const [entries] = useRecoilState(entrieState);
  const [touchY, setTouchY] = useState(-1);
  const [touchX, setTouchX] = useState(-1);
  const gaTrack = useGATrackEvent();

  const handleOnClick = (direction) => {
    if (direction === 'left') {
      setCurrentIndex(currentIndex - 1);
    } else if (direction === 'right') {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const canMoveLeft = currentIndex > 0;
  const canMoveRight = currentIndex + 1 < entries.length;
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchX(e.touches[0].clientX);
    setTouchY(e.touches[0].clientY);
  };

  const trackSwipe = () => {
    segmentTrackEvent(EVENTS.SWIPE_IN_USER_GENERATED_CONTENT_MODAL, {
      screenName: SCREENS.HOME,
      position: currentIndex,
    });
    gaTrack(Events.swipeInUserGeneratedContentModal, Categories.Home);
  };

  const handleTouchMove = (e) => {
    const diffX = touchX - e.touches[0].clientX;
    const diffY = touchY - e.touches[0].clientY;

    if (Math.abs(diffX) > 25 && Math.abs(diffY) <= 25) {
      if (diffX > 0 && canMoveRight) {
        /* right swipe */
        setCurrentIndex(currentIndex + 1);
        trackSwipe();
      } else if (diffX <= 0 && canMoveLeft) {
        /* left swipe */
        setCurrentIndex(currentIndex - 1);
        trackSwipe();
      }

      // reset points
      setTouchX(-1);
      setTouchY(-1);
    }
  };

  return (
    <>
      <Global styles={MobileModalHeightStyles} />
      <Modal
        data-testid="InstagramModal"
        isOpen={isOpen}
        onClose={onClose}
        isCentered
        size={size}
        scrollBehavior={isMobile ? 'inside' : undefined}
        closeOnOverlayClick={false}
      >
        <ModalOverlay bg="rgba(15, 15, 15, 0.8)" backdropBlur="2px" />
        <ModalContent
          // below code needed for image container to not exceed 1119 for xl desktop
          maxW={{ base: '100%', xl: '1119px' }}
          // below code needed for image container to not take over entire window in tablet
          width={{ base: '100%', md: '90%' }}
          // below code needed for image container to not have exceed height
          height={{ base: '100vh', lg: 'auto' }}
          // below code needed for mobile urlbar fix for safari
          minH={{ base: '-webkit-fill-available', md: 'unset' }}
          position="relative"
        >
          <Desktop>
            {canMoveLeft ? (
              <Box
                sx={StyledModalArrowStyles.variants['left']}
                onClick={() => handleOnClick('left')}
              >
                <ChevronLeftIcon color="white" boxSize="10" />
              </Box>
            ) : null}
            {canMoveRight ? (
              <Box
                sx={StyledModalArrowStyles.variants['right']}
                onClick={() => handleOnClick('right')}
              >
                <ChevronRightIcon color="white" boxSize="10" />
              </Box>
            ) : null}
            <ModalCloseButton />
            <UserGeneratedContentModalDesktop
              styles={styles}
              entry={entries[currentIndex]}
              key={currentIndex}
            />
            <UserGeneratedContentSocialLinks />
          </Desktop>
          <MobileAndTablet>
            <Box w="100%" position="relative" h="10" pb="2">
              <ModalCloseButton />
            </Box>
            <UserGeneratedContentModalMobile
              styles={styles}
              entry={entries[currentIndex]}
              handleTouchStart={handleTouchStart}
              handleTouchMove={handleTouchMove}
              key={currentIndex}
              canMoveLeft={canMoveLeft}
              canMoveRight={canMoveRight}
              handleOnClick={handleOnClick}
            />
          </MobileAndTablet>
        </ModalContent>
      </Modal>
    </>
  );
};

export default UserGeneratedContentModal;
